import { BaseApiUrl } from '../../../configVariables';
import apiClient from '../../../utils/apiClient';
import { TableContinuationToken } from '../../../model';
import { HaulType } from '../../../model';

export interface FlightNumberEntity {
    flightNumber: string;
    isUserCreated: boolean;
    expireOnUtc?: string;
    haulType?: HaulType;
}

export interface FlightNumbersResponse {
    search: string;
    items: FlightNumberEntity[];
    nextPageToken: TableContinuationToken;
}

const baseUrl = `${BaseApiUrl}/appsettings/flightnumbers`;

export interface UpsertFlightNumberResult {
    status: 'success' | 'duplicate' | 'error';
    message?: string;
    entity?: FlightNumberEntity;
}

const normalizeFlightNumber = (input: string): string => {
    const cleaned = input.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (!cleaned) {
        return '';
    }

    return cleaned.startsWith('BA') ? cleaned : `BA${cleaned}`;
};

export const deleteFlightNumber = async (flightNumber: string): Promise<void> => {
    await apiClient.delete(`${baseUrl}/${encodeURIComponent(flightNumber)}`);
};

export const fetchFlightNumbers = async (
    query: string = ''
): Promise<FlightNumbersResponse> => {
    const url = `${baseUrl}${query}`;
    const result = await apiClient.get<FlightNumbersResponse>(url);
    return result.data;
};

export const upsertFlightNumber = async (data: {
    flightNumber: string;
    isPermanent: boolean;
    haulType: HaulType;
}): Promise<UpsertFlightNumberResult> => {
    const payloadFlightNumber = normalizeFlightNumber(data.flightNumber);

    if (!payloadFlightNumber) {
        return {
            status: 'error',
            message: 'Flight number is required.',
        };
    }

    if (payloadFlightNumber.length > 10 || !/^BA[A-Za-z0-9]{0,8}$/.test(payloadFlightNumber)) {
        return {
            status: 'error',
            message: 'Flight number must start with BA and be no longer than 10 characters.',
        };
    }

    const payload = {
        flightNumber: payloadFlightNumber,
        makePermanent: data.isPermanent,
        haulType: data.haulType,
    };

    try {
        // Disable global error redirect so we can handle 409 locally
        const res = await apiClient.post<
            UpsertFlightNumberResult,
            { flightNumber: string; makePermanent: boolean; haulType: HaulType }
        >(baseUrl, payload, { skipErrorHandling: true });
        return res.data;
    } catch (err: any) {
        if (
            err.response?.status === 409 &&
            err.response?.data?.status === 'duplicate'
        ) {
            return err.response.data as UpsertFlightNumberResult;
        }
        throw err;
    }
};